import React from "react";
import { Link } from "react-router-dom";
import { Phone, Instagram, MessageCircle, Mail } from "lucide-react";
import { useSite, whatsappLink } from "@/context/SiteContext";

export const Footer = () => {
  const site = useSite();
  return (
    <footer className="border-t border-[color:var(--border-subtle)] bg-[#0B0B0C] pb-24 sm:pb-0">
      {/* Editorial CTA band */}
      <div className="border-b border-[color:var(--border-subtle)] noise-overlay">
        <div className="container-ascendia py-14 sm:py-16">
          <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-end">
            <div>
              <p className="kicker mb-4">The Ascendia Standard</p>
              <h2 className="display-2 text-white max-w-2xl">Every detail, prepared properly.</h2>
              <p className="mt-4 lede">Premium property detailing {site.service_area} — documented scopes, professional equipment and a final quality inspection on every job.</p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Link to="/contact" data-testid="footer-request-quote-button" className="btn-lux btn-primary">Request a Quote</Link>
              <a href={whatsappLink(site)} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-button" className="btn-lux btn-secondary"><MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp</a>
            </div>
          </div>
        </div>
      </div>
      <div className="container-ascendia py-14">
        <div className="grid gap-10 md:grid-cols-4">
          <div className="md:col-span-1">
            <div className="flex items-center gap-3">
              <img src={site.logo} alt="Ascendia Detail" className="h-9 w-9 object-contain" />
              <span className="font-display text-lg font-semibold text-white">Ascendia Detail</span>
            </div>
            <p className="mt-4 text-sm text-muted-c max-w-[28ch]">We don't just clean. We detail. Premium property detailing {site.service_area?.toLowerCase()}.</p>
          </div>
          <div>
            <p className="kicker mb-4">Services</p>
            <ul className="space-y-2 text-sm text-white/75">
              <li><Link to="/services/post-construction-cleaning" className="hover:text-gold-soft">Post-Construction Detail</Link></li>
              <li><Link to="/services/move-in-move-out-cleaning" className="hover:text-gold-soft">Move-In / Move-Out</Link></li>
              <li><Link to="/services/general-deep-cleaning" className="hover:text-gold-soft">Deep Reset</Link></li>
              <li><Link to="/services/property-detailing" className="hover:text-gold-soft">Private Residence Detail</Link></li>
              <li><Link to="/detailing-menu" className="hover:text-gold-soft">Focused Detail Menu</Link></li>
              <li><Link to="/property-intelligence" className="text-gold-soft hover:text-gold">Property Intelligence™</Link></li>
              <li><Link to="/packages" className="hover:text-gold-soft">Packages</Link></li>
              <li><Link to="/estimate" className="hover:text-gold-soft">Quick Estimate</Link></li>
            </ul>
          </div>
          <div>
            <p className="kicker mb-4">Company</p>
            <ul className="space-y-2 text-sm text-white/75">
              <li><Link to="/about" className="hover:text-gold-soft">About Us</Link></li>
              <li><Link to="/ascendia-standard" className="hover:text-gold-soft">The Ascendia Standard</Link></li>
              <li><Link to="/what-we-use" className="hover:text-gold-soft">What We Use</Link></li>
              <li><Link to="/trust" className="hover:text-gold-soft">Trust Center</Link></li>
              <li><Link to="/gallery" className="hover:text-gold-soft">Before &amp; After</Link></li>
              <li><Link to="/service-areas" className="hover:text-gold-soft">Service Areas</Link></li>
              <li><Link to="/for-professionals" className="hover:text-gold-soft">For Professionals</Link></li>
              <li><Link to="/faq" className="hover:text-gold-soft">FAQ</Link></li>
            </ul>
          </div>
          <div>
            <p className="kicker mb-4">Get in touch</p>
            <ul className="space-y-3 text-sm text-white/80">
              <li><a href={`tel:${site.phone}`} className="flex items-center gap-2 hover:text-gold-soft"><Phone className="h-4 w-4 text-gold-soft" /> {site.phone}</a></li>
              <li><a href={whatsappLink(site)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-gold-soft"><MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp</a></li>
              <li><a href={`mailto:${site.email}`} className="flex items-center gap-2 hover:text-gold-soft"><Mail className="h-4 w-4 text-gold-soft" /> Email</a></li>
              <li><a href={site.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-gold-soft"><Instagram className="h-4 w-4 text-gold-soft" /> {site.instagram_handle}</a></li>
            </ul>
            <Link to="/contact" className="mt-5 inline-flex rounded-xl bg-gold px-4 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft transition-colors">Request a Quote</Link>
          </div>
        </div>
        <div className="gold-hairline my-8" />
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-c">
          <p>© {new Date().getFullYear()} Ascendia Detail. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-gold-soft">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-gold-soft">Terms &amp; Conditions</Link>
            <Link to="/partners/login" className="hover:text-gold-soft">Partner Portal</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
